import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Buildings, Package, PlugsConnected, Truck, MagnifyingGlass,
  X, Eye, Flask, CheckCircle, CaretUp, CaretDown, CircleNotch, ArrowsClockwise, CalendarBlank, Clock,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FreshnessPill } from "@/components/FreshnessPill";
import { toast } from "@/components/ui/sonner";
import { supplierApi } from "@/lib/supplierPortalApi";
import { useSupplierAuth } from "@/contexts/SupplierAuthContext";
import { SupplierPortalLayout } from "./SupplierPortalLayout";
import { usePullCooldown } from "@/hooks/usePullCooldown";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  return String(detail);
}

function fmtMoney(amount, currency) {
  if (amount === null || amount === undefined) return "-";
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: currency || "INR", maximumFractionDigits: 2 }).format(amount);
  } catch {
    return amount.toFixed(2);
  }
}

// Oct 27 2026 fix (real user report: GRN "Line Value" looked wrong next
// to a 2-decimal "PO Price" - e.g. 0.054/EA rounded to "0.05", so
// 25000 x 0.05 (=1250) visibly didn't match the real line value of
// 1350.00). A per-unit rate can legitimately be a sub-cent fraction
// (SAP quotes some lines "per 100 units") - unlike a real money AMOUNT
// (subtotal/total), which should always stay at 2 decimals, a per-unit
// RATE needs enough decimals to actually multiply back to that amount.
function fmtUnitPrice(amount, currency) {
  if (amount === null || amount === undefined) return "-";
  const decimals = Math.abs(amount) < 1 ? 4 : 2;
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: currency || "INR", minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(amount);
  } catch {
    return amount.toFixed(decimals);
  }
}

// User's ask (Sep 2 2026): dates were wrapping onto 2 lines in the PO
// table ("2026-08-\n21") - short DD-MMM-YY format fits on one line.
function fmtDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleString("en-US", { month: "short" });
  const year = String(d.getFullYear()).slice(-2);
  return `${day}-${month}-${year}`;
}

// Sep 2 2026, user's explicit ask - Open Qty is now fetched live from
// SAP (fetch_open_po_quantities, refreshed ~every 20 min in the
// background) instead of only computed from this app's own shipment
// records, so a receipt made outside this app still counts. This
// shows the supplier/staff how fresh that SAP read is.
function timeAgo(isoString) {
  const diffMin = Math.max(0, Math.round((Date.now() - new Date(isoString).getTime()) / 60000));
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  return `${Math.round(diffMin / 60)}h ago`;
}

const cartKey = (po) => `${po.po_number}::${po.item_number}`;

export default function SupplierDashboardPage() {
  const { account, logout } = useSupplierAuth();
  const navigate = useNavigate();
  const { vendorCode } = useParams();
  const isImpersonating = !!(account?.testing_mode && vendorCode && vendorCode !== account?.vendor_code);
  const { onCooldown, remainingLabel, startCooldown } = usePullCooldown("supplier-dashboard-pull-cooldown", vendorCode);

  const [pos, setPos] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [liveSync, setLiveSync] = useState(true);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  // Sep 14 2026, user's explicit ask: toggle between only-open PO lines
  // (default, less clutter - matches "Your Open Purchase Orders" title)
  // and every cached line including fully-shipped ones.
  const [showAllPos, setShowAllPos] = useState(false);
  const [siteFilter, setSiteFilter] = useState("all");

  // Oct 2026, user's explicit ask ("set default view 30 days... give a
  // date filter so he can view old PO also") - the dashboard used to
  // always fetch every cached PO regardless of age. Defaults to the
  // last 30 days by PO Date; widening the range (or clearing it) is the
  // only way to see older POs - deliberately strict, per the user's own
  // choice, even for a still-open PO older than 30 days.
  const isoDaysAgo = (days) => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().slice(0, 10);
  };
  const [fromDate, setFromDate] = useState(isoDaysAgo(30));
  const [toDate, setToDate] = useState(isoDaysAgo(0));

  const [cart, setCart] = useState({});
  const [reviewOpen, setReviewOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [successCode, setSuccessCode] = useState(null);

  // Sep 20 2026, user's explicit ask ("add option to pull") - manual,
  // on-demand PO refresh instead of waiting out the ~10 min background
  // cache cycle. Polls the job status every 5s (real fetches take
  // 60-100s+ - see POST /supplier-portal/purchase-orders/refresh's own
  // docstring) and re-loads the PO list the moment it finishes.
  const [pulling, setPulling] = useState(false);

  // Entity restriction ("mrp vendor side changes.docx", Sep 2026): a
  // vendor with POs from more than one buyer entity (RI/RT) must pick ONE
  // to view/act on at a time - never a mixed table, never a mixed cart.
  const [selectedEntity, setSelectedEntity] = useState(null);

  const [detailPoNumber, setDetailPoNumber] = useState(null);

  // Sortable PO Date / Due Date columns (user's explicit ask).
  const [sortColumn, setSortColumn] = useState(null); // "po_date" | "due_date" | null
  const [sortDir, setSortDir] = useState("asc");

  const toggleSort = (column) => {
    if (sortColumn === column) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDir("asc");
    }
  };

  // TESTING ONLY (user's explicit ask, Aug 28 2026) - impersonate any
  // vendor's data via a search box. Gated by account.testing_mode
  // (mirrors backend SUPPLIER_PORTAL_TESTING_MODE) - REMOVE BEFORE LAUNCH.
  const [vendorSearch, setVendorSearch] = useState("");
  const [vendorResults, setVendorResults] = useState([]);
  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);

  const load = async () => {
    try {
      const params = isImpersonating ? { as_vendor: vendorCode, from_date: fromDate, to_date: toDate } : { from_date: fromDate, to_date: toDate };
      const [poRes, shipRes] = await Promise.all([
        supplierApi.get("/purchase-orders", { params }),
        supplierApi.get("/shipments", { params: isImpersonating ? { as_vendor: vendorCode } : {} }).catch(() => ({ data: { shipments: [] } })),
      ]);
      setPos(poRes.data.purchase_orders || []);
      setLiveSync(!!poRes.data.live_sync);
      setLastSyncedAt(poRes.data.last_synced_at || null);
      setShipments(shipRes.data.shipments || []);
      setError("");
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load your Purchase Orders.");
    } finally {
      setLoading(false);
    }
  };

  // Dashboard KPI bento cards (Vendor Portal redesign, design_guidelines.json).
  // Oct 12 2026 - "Open QC NCs" now comes live from QMS (real NMR+QN
  // open-count), replacing the earlier Preview/Demo mock data.
  const [openNcs, setOpenNcs] = useState(null);
  useEffect(() => {
    const params = isImpersonating ? { as_vendor: vendorCode } : {};
    Promise.all([
      supplierApi.get("/qms/nmrs", { params }).catch(() => ({ data: { nmrs: [] } })),
      supplierApi.get("/qms/qnotifications", { params }).catch(() => ({ data: { qnotifications: [] } })),
    ]).then(([nmrRes, qnRes]) => {
      const openNmrs = (nmrRes.data.nmrs || []).filter((n) => !["closed", "accepted"].includes((n.status || "").toLowerCase())).length;
      const openQns = (qnRes.data.qnotifications || []).filter((q) => q.status_label && q.status_label !== "Closed" && q.status_label !== "No CAP Required").length;
      setOpenNcs(openNmrs + openQns);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorCode]);

  const kpis = useMemo(() => ({
    activePos: new Set(pos.map((po) => po.po_number)).size,
    outstandingQty: pos.reduce((sum, po) => sum + (po.remaining_qty > 0 ? po.remaining_qty : 0), 0),
    inTransit: shipments.filter((s) => s.status === "in_transit").length,
    openNcs,
  }), [pos, shipments, openNcs]);

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorCode, fromDate, toDate]);

  // Oct 2026 fix (testing_agent iteration_248) - cart + entity selection
  // must only reset when switching VENDOR, not on every date-filter
  // nudge. These used to live in the effect above, which also fires on
  // fromDate/toDate - that wiped a multi-entity vendor's entity choice
  // (and their cart) back to the "Choose an entity to continue" empty
  // state after every single date change. The separate effect below
  // (already existed) already handles resetting selectedEntity if the
  // chosen entity's POs genuinely disappear from a new result set.
  useEffect(() => {
    setCart({});
    setSelectedEntity(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorCode]);

  // Every distinct buying entity (RI/RT) present across this vendor's own
  // POs - built from the same `buyer_code`/`buyer_entity_name` fields
  // already used for the "PO From" column.
  const entities = useMemo(() => {
    const map = new Map();
    pos.forEach((po) => {
      const code = po.buyer_code || "RT";
      if (!map.has(code)) map.set(code, po.buyer_entity_name || code);
    });
    return Array.from(map.entries()).map(([code, name]) => ({ code, name }));
  }, [pos]);

  // Auto-pick when there's only one entity to choose from (nothing to
  // force) - otherwise leave it unselected until the vendor picks one, and
  // reset if the previously-selected entity's POs disappeared (e.g. all
  // finished, or an impersonation switch).
  useEffect(() => {
    if (entities.length === 1) {
      setSelectedEntity(entities[0].code);
    } else if (selectedEntity && !entities.some((e) => e.code === selectedEntity)) {
      setSelectedEntity(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entities]);

  const chooseEntity = (code) => {
    if (code === selectedEntity) return;
    setSelectedEntity(code);
    setCart({});
    setSiteFilter("all");
  };

  const entityPos = useMemo(
    () => (selectedEntity ? pos.filter((po) => (po.buyer_code || "RT") === selectedEntity) : []),
    [pos, selectedEntity]
  );

  // Oct 2026, user's explicit ask - "only show sites they have in PO" in
  // the filter dropdown, scoped to the currently selected entity.
  const sites = useMemo(() => {
    const set = new Set(entityPos.map((po) => po.ship_to_site_id).filter(Boolean));
    return Array.from(set).sort();
  }, [entityPos]);

  // Oct 2026, user's explicit ask ("only 1 site PO per shipment code") -
  // same lock pattern as Act as Supplier's page: once the cart holds an
  // item, its Site locks the cart to that Site only.
  const lockedSiteId = useMemo(() => {
    const first = Object.values(cart)[0];
    return first?.ship_to_site_id || null;
  }, [cart]);

  useEffect(() => {
    if (!account?.testing_mode || !vendorSearch.trim()) {
      setVendorResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await supplierApi.get("/testing/vendor-directory", { params: { q: vendorSearch } });
        setVendorResults(data.vendors || []);
      } catch {
        setVendorResults([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [vendorSearch, account?.testing_mode]);

  const filteredPos = useMemo(() => {
    const scoped = showAllPos ? entityPos : entityPos.filter((po) => po.remaining_qty > 0);
    const siteScoped = scoped.filter((po) => siteFilter === "all" || po.ship_to_site_id === siteFilter);
    const q = search.trim().toLowerCase();
    if (!q) return siteScoped;
    return siteScoped.filter((po) => [po.po_number, po.item_number, po.product_id, po.description].some((v) => (v || "").toString().toLowerCase().includes(q)));
  }, [entityPos, search, showAllPos, siteFilter]);

  const sortedPos = useMemo(() => {
    if (!sortColumn) return filteredPos;
    const withIndex = filteredPos.map((po, idx) => ({ po, idx }));
    withIndex.sort((a, b) => {
      const aTime = a.po[sortColumn] ? new Date(a.po[sortColumn]).getTime() : 0;
      const bTime = b.po[sortColumn] ? new Date(b.po[sortColumn]).getTime() : 0;
      if (aTime === bTime) return a.idx - b.idx; // stable - keeps same-PO rows grouped
      return sortDir === "asc" ? aTime - bTime : bTime - aTime;
    });
    return withIndex.map((w) => w.po);
  }, [filteredPos, sortColumn, sortDir]);

  // Bulk Cart Add - group by PO Number (open items only) so a single
  // "select all" toggle can act on the whole group; anchored to the
  // first OPEN row of each group (not just the first row overall) and
  // pre-computed once here instead of re-filtering on every click/render
  // (code review feedback, iteration_124).
  const rowsWithPoGroup = useMemo(() => {
    // Feb 2026, user's explicit ask - a line SAP hasn't verified yet
    // (po.sap_verified_at is null) is excluded from the "select all"
    // group entirely; its own checkbox is also disabled below (see
    // the backend's matching hard-block in _resolve_items).
    const groups = {};
    sortedPos.forEach((po) => {
      if (po.remaining_qty > 0 && po.sap_verified_at) {
        groups[po.po_number] = groups[po.po_number] || [];
        groups[po.po_number].push(po);
      }
    });
    const seenOpenPo = new Set();
    return sortedPos.map((po) => {
      const isOpen = po.remaining_qty > 0 && !!po.sap_verified_at;
      const isFirstOpenOfPo = isOpen && !seenOpenPo.has(po.po_number);
      if (isOpen) seenOpenPo.add(po.po_number);
      const openRows = groups[po.po_number] || [];
      return { ...po, _isFirstOpenOfPo: isFirstOpenOfPo, _poOpenRows: openRows };
    });
  }, [sortedPos]);

  const detailItems = useMemo(
    () => pos.filter((po) => po.po_number === detailPoNumber)
      .slice()
      .sort((a, b) => (a.item_number || "").localeCompare(b.item_number || "", undefined, { numeric: true })),
    [pos, detailPoNumber]
  );

  const toggleCartItem = (po, checked) => {
    if (checked && !po.sap_verified_at) return; // Feb 2026 - mirrors backend hard-block, see rowsWithPoGroup's comment
    setCart((prev) => {
      const next = { ...prev };
      const key = cartKey(po);
      if (checked) {
        next[key] = { po_number: po.po_number, item_number: po.item_number, product_id: po.product_id, ship_to_site_id: po.ship_to_site_id, description: po.description, unit_of_measure: po.unit_of_measure, remaining_qty: po.remaining_qty, extra_tolerance_pct: po.extra_tolerance_pct, unit_price: po.unit_price, currency: po.currency, ship_qty: "" };
      } else {
        delete next[key];
      }
      return next;
    });
  };

  const isPoFullySelected = (rows) => rows.length > 0 && rows.every((p) => !!cart[cartKey(p)]);
  const toggleSelectAllForPo = (rows) => {
    const allSelected = isPoFullySelected(rows);
    setCart((prev) => {
      const next = { ...prev };
      rows.forEach((p) => {
        const key = cartKey(p);
        if (allSelected) {
          delete next[key];
        } else {
          // Left blank, same as an individual checkbox - a full-remaining-qty
          // prefill across every row in one click was flagged as risky
          // (code review feedback, iteration_124).
          next[key] = { po_number: p.po_number, item_number: p.item_number, product_id: p.product_id, ship_to_site_id: p.ship_to_site_id, description: p.description, unit_of_measure: p.unit_of_measure, remaining_qty: p.remaining_qty, extra_tolerance_pct: p.extra_tolerance_pct, unit_price: p.unit_price, currency: p.currency, ship_qty: "" };
        }
      });
      return next;
    });
  };

  const updateCartQty = (key, value) => {
    setCart((prev) => ({ ...prev, [key]: { ...prev[key], ship_qty: value } }));
  };

  const removeFromCart = (key) => {
    setCart((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const cartItems = Object.entries(cart).map(([key, v]) => ({ key, ...v }));
  // Feb 2026, user's explicit ask - total of price*qty across every
  // selected line, shown in the Review Shipment dialog.
  const cartTotal = cartItems.reduce((sum, c) => sum + (c.unit_price != null ? c.unit_price * (parseFloat(c.ship_qty) || 0) : 0), 0);
  const cartCurrency = cartItems.find((c) => c.currency)?.currency;

  const pullLatestPos = async () => {
    setPulling(true);
    try {
      const params = isImpersonating ? { as_vendor: vendorCode } : {};
      const { data } = await supplierApi.post("/purchase-orders/refresh", {}, { params });
      startCooldown();
      toast.success(`Pulled the latest Purchase Orders from SAP (${data.po_count} on file).`);
      await load();
    } catch (e) {
      if (e?.response?.status === 429) {
        const match = /wait (\d+) more minute/i.exec(e?.response?.data?.detail || "");
        startCooldown(match ? Number(match[1]) : undefined);
      }
      toast.error("Could not pull the latest POs from SAP", { description: e.response?.data?.detail || e.message });
    } finally {
      setPulling(false);
    }
  };

  const submitShipment = async () => {
    setSubmitting(true);
    setSubmitError("");
    try {
      const items = cartItems.map((c) => ({ po_number: c.po_number, item_number: c.item_number, ship_qty: parseFloat(c.ship_qty) }));
      const params = isImpersonating ? { as_vendor: vendorCode } : {};
      const { data } = await supplierApi.post("/shipments", { items }, { params });
      setCart({});
      setConfirmOpen(false);
      setReviewOpen(false);
      setTimeout(() => setSuccessCode(data._id), 200);
      load();
    } catch (e) {
      setSubmitError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SupplierPortalLayout
      active="dashboard"
      vendorCode={vendorCode}
      isImpersonating={isImpersonating}
      pageTitle="Vendor Control Center"
      pageSubtitle={`${account?.email || ""}`}
    >
      <div data-testid="supplier-dashboard-page">
        {account?.testing_mode && (
          <div className="relative max-w-xs mb-4" data-testid="supplier-vendor-impersonation-search">
            <div className="flex items-center gap-1 bg-white border border-[#E2E8F0] rounded-md px-2 py-1">
              <Flask size={13} className="text-slate-400 shrink-0" />
              <Input
                placeholder="Testing: view as vendor..."
                value={vendorSearch}
                onChange={(e) => { setVendorSearch(e.target.value); setVendorDropdownOpen(true); }}
                onFocus={() => setVendorDropdownOpen(true)}
                className="h-6 border-none bg-transparent placeholder:text-slate-400 focus-visible:ring-0 px-1 text-xs"
                data-testid="supplier-vendor-search-input"
              />
            </div>
            {vendorDropdownOpen && vendorResults.length > 0 && (
              <div className="absolute top-full left-0 mt-1 w-full bg-white text-[#0F172A] rounded-md shadow-lg border border-[#E2E8F0] max-h-56 overflow-y-auto z-20" data-testid="supplier-vendor-search-results">
                {vendorResults.map((v) => (
                  <button
                    key={v.vendor_code}
                    onClick={() => { setVendorDropdownOpen(false); setVendorSearch(""); navigate(`/supplier-portal/dashboard/${v.vendor_code}`); }}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-[#F0F4F8] flex justify-between gap-2"
                    data-testid={`supplier-vendor-search-result-${v.vendor_code}`}
                  >
                    <span className="truncate">{v.vendor_name || v.vendor_code}</span>
                    <span className="font-data text-[#475569] shrink-0">{v.vendor_code}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="supplier-dashboard-kpi-row">
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-4" data-testid="supplier-kpi-active-pos">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Active POs</div>
            <div className="text-2xl md:text-3xl font-extrabold text-[#0F172A] mt-1">{loading ? "-" : kpis.activePos}</div>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-4" data-testid="supplier-kpi-outstanding-qty">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Outstanding Qty</div>
            <div className="text-2xl md:text-3xl font-extrabold text-[#0F172A] mt-1">{loading ? "-" : kpis.outstandingQty}</div>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-4" data-testid="supplier-kpi-in-transit">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">In-Transit Shipments</div>
            <div className="text-2xl md:text-3xl font-extrabold text-[#1E40AF] mt-1">{loading ? "-" : kpis.inTransit}</div>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-4" data-testid="supplier-kpi-open-ncs">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Open QC NCs</div>
            <div className="text-2xl md:text-3xl font-extrabold text-[#9A3412] mt-1">{kpis.openNcs == null ? "-" : kpis.openNcs}</div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-heading text-lg font-bold text-[#0F172A]">Your {showAllPos ? "" : "Open "}Purchase Orders</h2>
          <div className="flex items-center gap-3 flex-wrap">
            <Button
              variant="outline"
              onClick={pullLatestPos}
              disabled={pulling || onCooldown}
              className="h-8 rounded-sm border-[#E2E8F0] text-xs"
              title={onCooldown ? "Pulled recently - wait for the 10 minute cooldown to finish." : "Don't want to wait? Pull the newest Purchase Orders from SAP right now."}
              data-testid="supplier-pull-latest-pos-button"
            >
              {pulling ? <CircleNotch size={13} className="mr-1.5 animate-spin" /> : <ArrowsClockwise size={13} className="mr-1.5" />}
              {pulling ? "Pulling from SAP..." : onCooldown ? remainingLabel : "Pull Latest POs"}
            </Button>
            <div className="flex items-center rounded-sm border border-[#E2E8F0] bg-white p-0.5" data-testid="supplier-po-filter-toggle">
              <button
                type="button"
                onClick={() => setShowAllPos(false)}
                className={`text-xs font-semibold px-3 py-1 rounded-sm transition-colors duration-150 ${!showAllPos ? "bg-[#1E40AF] text-white" : "text-[#475569] hover:text-[#0F172A]"}`}
                data-testid="supplier-po-filter-open-only"
              >
                Open Only
              </button>
              <button
                type="button"
                onClick={() => setShowAllPos(true)}
                className={`text-xs font-semibold px-3 py-1 rounded-sm transition-colors duration-150 ${showAllPos ? "bg-[#1E40AF] text-white" : "text-[#475569] hover:text-[#0F172A]"}`}
                data-testid="supplier-po-filter-all"
              >
                All (incl. Fully Shipped)
              </button>
            </div>
            <div className="flex items-center gap-1.5 rounded-sm border border-[#E2E8F0] bg-white px-2 h-8" data-testid="supplier-po-date-filter">
              <CalendarBlank size={14} className="text-[#475569] shrink-0" />
              <input
                type="date"
                value={fromDate}
                max={toDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="text-[12px] text-[#344054] border-none outline-none w-[108px]"
                data-testid="supplier-po-date-filter-from"
              />
              <span className="text-[#98A2B3] text-xs">to</span>
              <input
                type="date"
                value={toDate}
                min={fromDate}
                onChange={(e) => setToDate(e.target.value)}
                className="text-[12px] text-[#344054] border-none outline-none w-[108px]"
                data-testid="supplier-po-date-filter-to"
              />
              {(fromDate !== isoDaysAgo(30) || toDate !== isoDaysAgo(0)) && (
                <button
                  type="button"
                  onClick={() => { setFromDate(isoDaysAgo(30)); setToDate(isoDaysAgo(0)); }}
                  className="text-[11px] font-semibold text-[#1E40AF] hover:underline pl-1"
                  title="Reset to the last 30 days"
                  data-testid="supplier-po-date-filter-reset"
                >
                  Reset
                </button>
              )}
            </div>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#475569]" />
              <Input
                placeholder="Search PO #, Item #, or description..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 pl-8 pr-2 w-72 text-[13px] rounded-sm border-[#E2E8F0] focus-visible:border-[#1E40AF] focus-visible:ring-1 focus-visible:ring-[#1E40AF]"
                data-testid="supplier-po-search-input"
              />
            </div>
            <Select value={siteFilter} onValueChange={setSiteFilter}>
              <SelectTrigger className="h-8 w-40 text-[13px] rounded-sm border-[#E2E8F0]" data-testid="supplier-po-site-filter">
                <SelectValue placeholder="Filter by Site" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="supplier-po-site-filter-all">All Sites</SelectItem>
                {sites.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`supplier-po-site-filter-${s}`}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FreshnessPill lastSyncedAt={lastSyncedAt} isFresh={liveSync} testId="supplier-po-freshness-pill" />
          </div>
        </div>

        {loading && (
          <div className="mt-8 flex items-center gap-2.5 bg-white border border-[#E2E8F0] rounded-lg px-4 py-3 text-sm text-[#475569]" data-testid="supplier-dashboard-loading">
            <CircleNotch size={16} className="animate-spin text-[#1E40AF]" />
            <span>Pulling live Open Qty from SAP for your Purchase Orders...</span>
          </div>
        )}

        {!loading && entities.length > 1 && (
          <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="supplier-entity-switcher">
            <span className="text-xs font-semibold text-[#475569] uppercase">Entity</span>
            {entities.map((e) => (
              <button
                key={e.code}
                onClick={() => chooseEntity(e.code)}
                className={`text-xs font-semibold px-3 py-1.5 rounded-sm border transition-colors duration-150 ${
                  selectedEntity === e.code
                    ? "bg-[#1E40AF] border-[#1E40AF] text-white"
                    : "bg-white border-[#E2E8F0] text-[#0F172A] hover:border-[#1E40AF]"
                }`}
                data-testid={`supplier-entity-option-${e.code}`}
              >
                {e.name} ({e.code})
              </button>
            ))}
          </div>
        )}

        {!loading && entities.length > 1 && !selectedEntity && (
          <div className="mt-6 bg-white border border-[#E2E8F0] rounded-sm shadow-sm p-8 text-center" data-testid="supplier-entity-pick-required">
            <Buildings size={32} weight="fill" className="text-[#1E40AF] mx-auto mb-3" />
            <h2 className="font-heading font-bold text-[#0F172A]">Choose an entity to continue</h2>
            <p className="text-sm text-[#475569] mt-1 max-w-md mx-auto">
              You have open Purchase Orders from more than one entity - pick one above to view and act on its POs.
            </p>
          </div>
        )}

        {!loading && !liveSync && pos.length > 0 && (
          <div className="mt-4 text-xs text-[#8A6116] bg-[#E3A008]/15 border border-[#E3A008]/30 rounded-sm px-3 py-2" data-testid="supplier-dashboard-stale-banner">
            Showing the last synced data - live SAP connection is still being set up.
          </div>
        )}

        {!loading && !liveSync && pos.length === 0 && (
          <div className="mt-6 bg-white border border-[#E2E8F0] rounded-sm shadow-sm p-8 text-center" data-testid="supplier-dashboard-not-configured">
            <PlugsConnected size={32} weight="fill" className="text-[#E3A008] mx-auto mb-3" />
            <h2 className="font-heading font-bold text-[#0F172A]">SAP Connection Coming Soon</h2>
            <p className="text-sm text-[#475569] mt-1 max-w-md mx-auto">
              Your account is approved. We're still connecting this portal to SAP - your Purchase Orders will appear here automatically once that's live.
            </p>
          </div>
        )}

        {!loading && error && (
          <div className="mt-6 text-sm text-[#991B1B] bg-[#991B1B]/10 border border-[#991B1B]/30 rounded-sm px-4 py-3" data-testid="supplier-dashboard-error">{error}</div>
        )}

        {!loading && liveSync && !error && pos.length === 0 && (
          <div className="mt-6 bg-white border border-[#E2E8F0] rounded-sm shadow-sm p-8 text-center" data-testid="supplier-dashboard-empty">
            <Package size={32} weight="fill" className="text-[#475569] mx-auto mb-3" />
            <p className="text-sm text-[#475569]">No open Purchase Orders right now.</p>
          </div>
        )}

        {!loading && filteredPos.length > 0 && (
          <div className="mt-4 bg-white border border-[#E2E8F0] rounded-sm shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] overflow-x-auto" data-testid="supplier-dashboard-po-table">
            <table className="w-full text-xs border-collapse">
              <thead className="bg-[#F1F5F9] text-[#344054] text-xs font-bold font-heading uppercase tracking-wide">
                <tr>
                  <th className="border border-[#E2E8F0] p-1.5 text-left w-8"></th>
                  <th className="border border-[#E2E8F0] p-1.5 text-left">PO Number</th>
                  <th className="border border-[#E2E8F0] p-1.5 text-left">Item Code</th>
                  <th className="border border-[#E2E8F0] p-1.5 text-left">Description</th>
                  <th className="border border-[#E2E8F0] p-1.5 text-left">PO From</th>
                  <th className="border border-[#E2E8F0] p-1.5 text-left">Ship To</th>
                  <th className="border border-[#E2E8F0] p-1.5 text-left">
                    <button
                      onClick={() => toggleSort("po_date")}
                      className="flex items-center gap-1 hover:text-[#1E40AF] uppercase"
                      data-testid="supplier-po-sort-po-date"
                    >
                      PO Date
                      {sortColumn === "po_date" ? (sortDir === "asc" ? <CaretUp size={11} weight="bold" /> : <CaretDown size={11} weight="bold" />) : <CaretUp size={11} className="opacity-30" />}
                    </button>
                  </th>
                  <th className="border border-[#E2E8F0] p-1.5 text-right">PO Qty</th>
                  <th className="border border-[#E2E8F0] p-1.5 text-right">Unit Price</th>
                  <th className="border border-[#E2E8F0] p-1.5 text-right">Subtotal</th>
                  <th className="border border-[#E2E8F0] p-1.5 text-right">In Transit Qty</th>
                  <th className="border border-[#E2E8F0] p-1.5 text-right">Received Qty</th>
                  <th className="border border-[#E2E8F0] p-1.5 text-right">Open Qty</th>
                  <th className="border border-[#E2E8F0] p-1.5 text-left">
                    <button
                      onClick={() => toggleSort("due_date")}
                      className="flex items-center gap-1 hover:text-[#1E40AF] uppercase"
                      data-testid="supplier-po-sort-due-date"
                    >
                      Due Date
                      {sortColumn === "due_date" ? (sortDir === "asc" ? <CaretUp size={11} weight="bold" /> : <CaretDown size={11} weight="bold" />) : <CaretUp size={11} className="opacity-30" />}
                    </button>
                  </th>
                  <th className="border border-[#E2E8F0] p-1.5 text-right w-28">Ship Qty</th>
                  <th className="border border-[#E2E8F0] p-1.5 text-left">SAP Verified</th>
                </tr>
              </thead>
              <tbody>
                {rowsWithPoGroup.map((po, i) => {
                  const key = cartKey(po);
                  const inCart = !!cart[key];
                  const siteBlocked = !!lockedSiteId && po.ship_to_site_id && po.ship_to_site_id !== lockedSiteId;
                  const groupBlocked = po._poOpenRows?.some((p) => !!lockedSiteId && p.ship_to_site_id && p.ship_to_site_id !== lockedSiteId);
                  return (
                    <tr key={key} className="bg-white odd:bg-[#F9FAFB] hover:bg-[#F0F4F8] transition-colors duration-150" data-testid={`supplier-po-row-${po.po_number}-${po.item_number}`}>
                      <td className="border border-[#E2E8F0] px-2 py-1">
                        {po.remaining_qty > 0 ? (
                          <span
                            // Feb 2026 fix (testing agent feedback) - a `title` on a
                            // DISABLED Radix Checkbox never shows in most browsers
                            // (disabled elements don't fire hover/pointer events) -
                            // the tooltip now lives on this wrapping span instead.
                            title={!po.sap_verified_at ? "SAP has not confirmed this line's Open Quantity yet - try again in a few minutes" : undefined}
                          >
                            <Checkbox
                              checked={inCart}
                              disabled={siteBlocked || !po.sap_verified_at}
                              onCheckedChange={(c) => toggleCartItem(po, !!c)}
                              data-testid={`supplier-po-checkbox-${po.po_number}-${po.item_number}`}
                            />
                          </span>
                        ) : (
                          <span className="text-[#94A3B8]" title="Nothing left to ship on this line">—</span>
                        )}
                      </td>
                      <td className="border border-[#E2E8F0] px-2 py-1 font-data">
                        <button
                          onClick={() => setDetailPoNumber(po.po_number)}
                          className="text-[#1E40AF] hover:underline underline-offset-2 flex items-center gap-1"
                          data-testid={`supplier-po-detail-link-${po.po_number}`}
                        >
                          <Eye size={12} /> {po.po_number}
                        </button>
                        {po.sap_po_number && (
                          <div className="text-[10px] text-[#64748B] font-sans" data-testid={`supplier-po-printed-number-${po.po_number}-${po.item_number}`}>Printed PO #: {po.sap_po_number}</div>
                        )}
                        {po._isFirstOpenOfPo && po._poOpenRows.length > 1 && (
                          <button
                            onClick={() => toggleSelectAllForPo(po._poOpenRows)}
                            disabled={groupBlocked && !isPoFullySelected(po._poOpenRows)}
                            className="text-xs text-[#1E40AF] hover:underline underline-offset-2 mt-0.5 block whitespace-nowrap disabled:text-[#94A3B8] disabled:no-underline disabled:cursor-not-allowed"
                            data-testid={`supplier-po-select-all-${po.po_number}`}
                          >
                            {isPoFullySelected(po._poOpenRows) ? "Deselect all" : `Select all ${po._poOpenRows.length} items`}
                          </button>
                        )}
                      </td>
                      <td className="border border-[#E2E8F0] px-2 py-1 font-data text-xs" data-testid={`supplier-po-item-code-${po.po_number}-${po.item_number}`}>
                        {po.product_id || <span className="text-[#94A3B8] italic font-sans" title="This line is a Job Work item, not linked to a Product Master in SAP - no Item Code exists to show.">Job Work (no SAP code)</span>}
                      </td>
                      <td className="border border-[#E2E8F0] px-2 py-1">{po.description || "-"}</td>
                      <td className="border border-[#E2E8F0] px-2 py-1 text-xs">{po.buyer_entity_name}</td>
                      <td className="border border-[#E2E8F0] px-2 py-1" data-testid={`supplier-po-ship-to-${po.po_number}-${po.item_number}`}>
                        {po.ship_to_site_id ? (
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded-sm text-[11px] font-data font-semibold ${siteBlocked ? "bg-[#FEE2E2] text-[#991B1B]" : "bg-[#DCFCE7] text-[#166534]"}`}
                            title={siteBlocked ? `Locked to Site ${lockedSiteId} - remove those items first to ship to ${po.ship_to_site_id} instead` : undefined}
                          >
                            {po.ship_to_site_id}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="border border-[#E2E8F0] px-2 py-1 font-data text-xs whitespace-nowrap">{fmtDate(po.po_date)}</td>
                      <td className="border border-[#E2E8F0] px-2 py-1 text-right font-data text-xs" data-testid={`supplier-po-qty-${po.po_number}-${po.item_number}`}>
                        {po.po_qty} {po.unit_of_measure}
                      </td>
                      <td className="border border-[#E2E8F0] px-2 py-1 text-right font-data text-xs">{fmtUnitPrice(po.unit_price, po.currency)}</td>
                      <td className="border border-[#E2E8F0] px-2 py-1 text-right font-data text-xs">{fmtMoney(po.subtotal, po.currency)}</td>
                      <td className="border border-[#E2E8F0] px-2 py-1 text-right font-data" data-testid={`supplier-po-in-transit-qty-${po.po_number}-${po.item_number}`}>
                        {po.in_transit_qty > 0 ? (
                          <button
                            onClick={() => navigate(`/supplier-portal/shipments/${vendorCode}?po=${po.po_number}`)}
                            className="text-[#1E40AF] hover:underline underline-offset-2"
                            title="View/edit this PO's in-transit shipment(s)"
                            data-testid={`supplier-po-in-transit-qty-link-${po.po_number}-${po.item_number}`}
                          >
                            {po.in_transit_qty} {po.unit_of_measure}
                          </button>
                        ) : (
                          <span className="text-[#94A3B8]">0</span>
                        )}
                      </td>
                      <td className="border border-[#E2E8F0] px-2 py-1 text-right font-data text-[#065F46]" data-testid={`supplier-po-received-qty-${po.po_number}-${po.item_number}`}>
                        {po.received_qty} {po.unit_of_measure}
                      </td>
                      <td className="border border-[#E2E8F0] px-2 py-1 text-right font-data" data-testid={`supplier-po-open-qty-${po.po_number}-${po.item_number}`}>
                        {po.remaining_qty} {po.unit_of_measure}
                        {po.extra_tolerance_pct > 0 && (
                          <div className="text-[10px] text-[#166534] font-sans font-semibold whitespace-nowrap" data-testid={`supplier-po-tolerance-badge-${po.po_number}-${po.item_number}`} title="This PO's source PR was approved with an Extra % over-delivery allowance - you can ship above Open Qty up to this limit.">
                            +{po.extra_tolerance_pct}% extra OK
                          </div>
                        )}
                      </td>
                      <td className="border border-[#E2E8F0] px-2 py-1 font-data text-xs whitespace-nowrap">{fmtDate(po.due_date)}</td>
                      <td className="border border-[#E2E8F0] px-2 py-1 text-right">
                        {po.remaining_qty > 0 ? (
                          <Input
                            type="number"
                            disabled={!inCart}
                            value={inCart ? cart[key].ship_qty : ""}
                            onChange={(e) => updateCartQty(key, e.target.value)}
                            className="h-7 w-24 text-right rounded-sm border-[#E2E8F0] text-xs"
                            data-testid={`supplier-po-qty-input-${po.po_number}-${po.item_number}`}
                          />
                        ) : (
                          <span className="text-[#94A3B8] text-xs" data-testid={`supplier-po-qty-input-${po.po_number}-${po.item_number}`}>Fully shipped</span>
                        )}
                      </td>
                      <td className="border border-[#E2E8F0] px-2 py-1 whitespace-nowrap">
                        <div className={`text-[10px] font-sans flex items-center gap-1 ${po.sap_verified_at ? "text-[#94A3B8]" : "text-[#B54708] font-semibold"}`} data-testid={`supplier-po-sap-verified-${po.po_number}-${po.item_number}`}>
                          {po.sap_verified_at ? (
                            <>
                              <CheckCircle size={12} weight="fill" className="text-[#12B76A]" data-testid={`supplier-po-sap-verified-tick-${po.po_number}-${po.item_number}`} />
                              SAP-verified {timeAgo(po.sap_verified_at)}
                            </>
                          ) : (
                            <>
                              <Clock size={12} weight="fill" data-testid={`supplier-po-sap-unverified-icon-${po.po_number}-${po.item_number}`} />
                              Not yet verified - can't ship
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {!loading && (entities.length <= 1 || selectedEntity) && pos.length > 0 && filteredPos.length === 0 && (
          <div className="mt-6 text-sm text-[#475569] text-center" data-testid="supplier-po-search-empty">
            {search ? `No POs match "${search}".` : "No open PO lines right now - everything's fully shipped. Switch to \"All (incl. Fully Shipped)\" to see them."}
          </div>
        )}

        {cartItems.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 md:left-60 bg-white border-t border-[#E2E8F0] shadow-[0_-2px_8px_0_rgba(16,24,40,0.08)] px-4 py-3 flex items-center justify-between z-20" data-testid="supplier-cart-bar">
          <span className="text-sm text-[#0F172A] font-semibold" data-testid="supplier-cart-count">{cartItems.length} item{cartItems.length > 1 ? "s" : ""} selected</span>
          <div className="flex items-center gap-2">
            <Button onClick={() => setCart({})} variant="outline" className="rounded-sm border-[#DC2626] text-[#DC2626] hover:bg-[#FEF2F2] hover:text-[#DC2626] transition-colors duration-150" data-testid="supplier-clear-selections-button">
              <X size={14} className="mr-1" /> Clear Selections
            </Button>
            <Button onClick={() => setReviewOpen(true)} className="rounded-sm bg-[#1E40AF] hover:bg-[#1E3A8A] transition-colors duration-150" data-testid="supplier-review-shipment-button">
              <Truck size={14} className="mr-1" /> Review Shipment
            </Button>
          </div>
        </div>
      )}

      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="rounded-sm max-w-3xl" data-testid="supplier-review-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Review Shipment</DialogTitle>
            <DialogDescription>Confirm the items and quantities before generating your shipment code.</DialogDescription>
          </DialogHeader>
          <div className="max-h-72 overflow-y-auto overflow-x-auto border border-[#E2E8F0] rounded-sm">
            <table className="w-full text-[11px]">
              <thead className="bg-[#F8FAFC] sticky top-0">
                <tr>
                  <th className="border-b border-[#E2E8F0] p-1.5 text-left whitespace-nowrap">PO / Item</th>
                  <th className="border-b border-[#E2E8F0] p-1.5 text-left whitespace-nowrap">Item Code</th>
                  <th className="border-b border-[#E2E8F0] p-1.5 text-left">Description</th>
                  <th className="border-b border-[#E2E8F0] p-1.5 text-left whitespace-nowrap">Ship To</th>
                  <th className="border-b border-[#E2E8F0] p-1.5 text-right whitespace-nowrap">Open Qty</th>
                  <th className="border-b border-[#E2E8F0] p-1.5 text-right whitespace-nowrap">Item Price</th>
                  <th className="border-b border-[#E2E8F0] p-1.5 text-right whitespace-nowrap">Ship Qty</th>
                  <th className="border-b border-[#E2E8F0] p-1.5 text-right whitespace-nowrap">Amount</th>
                  <th className="border-b border-[#E2E8F0] p-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {cartItems.map((c) => {
                  const amount = c.unit_price != null ? c.unit_price * (parseFloat(c.ship_qty) || 0) : null;
                  return (
                    <tr key={c.key} className="border-t border-[#E2E8F0]" data-testid={`supplier-review-row-${c.key}`}>
                      <td className="px-1.5 py-1.5 font-data whitespace-nowrap">PO {c.po_number} · {c.item_number}</td>
                      <td className="px-1.5 py-1.5 font-data whitespace-nowrap" data-testid={`supplier-review-row-meta-${c.key}`}>{c.product_id || "—"}</td>
                      <td className="px-1.5 py-1.5">{c.description || "—"}</td>
                      <td className="px-1.5 py-1.5 font-data whitespace-nowrap">{c.ship_to_site_id || "—"}</td>
                      <td className="px-1.5 py-1.5 text-right font-data whitespace-nowrap text-[#475569]">
                        {c.remaining_qty ?? "—"} {c.unit_of_measure}
                        {c.extra_tolerance_pct > 0 && (
                          <div className="text-[10px] text-[#166534] font-sans font-semibold" data-testid={`supplier-review-tolerance-badge-${c.key}`}>+{c.extra_tolerance_pct}% extra OK</div>
                        )}
                      </td>
                      <td className="px-1.5 py-1.5 text-right font-data whitespace-nowrap text-[#475569]" data-testid={`supplier-review-price-${c.key}`}>
                        {fmtUnitPrice(c.unit_price, c.currency)}
                      </td>
                      <td className="px-1.5 py-1.5">
                        <div className="flex items-center justify-end gap-1">
                          <Input
                            type="number"
                            value={c.ship_qty}
                            onChange={(e) => updateCartQty(c.key, e.target.value)}
                            className="h-8 w-20 text-right rounded-sm border-[#E2E8F0] text-xs"
                            data-testid={`supplier-review-qty-input-${c.key}`}
                          />
                          <span className="text-[#475569] w-8">{c.unit_of_measure}</span>
                        </div>
                      </td>
                      <td className="px-1.5 py-1.5 text-right font-data font-semibold whitespace-nowrap" data-testid={`supplier-review-amount-${c.key}`}>
                        {amount != null ? fmtMoney(amount, c.currency) : "-"}
                      </td>
                      <td className="px-1.5 py-1.5 text-center">
                        <button onClick={() => removeFromCart(c.key)} className="text-[#991B1B] hover:bg-[#991B1B]/10 rounded-sm p-1" data-testid={`supplier-review-remove-${c.key}`}>
                          <X size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-[#CBD3DB] bg-[#F8FAFC]">
                  <td colSpan={7} className="px-1.5 py-2 text-right font-semibold text-[#344054] whitespace-nowrap">Total</td>
                  <td className="px-1.5 py-2 text-right font-data font-bold text-[#1E40AF] whitespace-nowrap" data-testid="supplier-review-total">{fmtMoney(cartTotal, cartCurrency)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setReviewOpen(false)}>Cancel</Button>
            <Button
              onClick={() => setConfirmOpen(true)}
              disabled={cartItems.length === 0}
              className="rounded-sm bg-[#1E40AF] hover:bg-[#1E3A8A] transition-colors duration-150"
              data-testid="supplier-review-confirm-button"
            >
              Confirm Shipment
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="rounded-sm" data-testid="supplier-confirm-generate-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Generate Shipment Code?</DialogTitle>
            <DialogDescription>
              This will create one shipment covering {cartItems.length} item{cartItems.length > 1 ? "s" : ""} across {new Set(cartItems.map((c) => c.po_number)).size} PO(s). You can still edit its contents until it's received - are you sure?
            </DialogDescription>
          </DialogHeader>
          {submitError && <div className="text-sm text-[#991B1B]" data-testid="supplier-submit-error">{submitError}</div>}
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button onClick={submitShipment} disabled={submitting} className="rounded-sm bg-[#1E40AF] hover:bg-[#1E3A8A] transition-colors duration-150" data-testid="supplier-confirm-generate-button">
              {submitting ? "Generating..." : "Yes, Generate Code"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!successCode} onOpenChange={(o) => !o && setSuccessCode(null)}>
        <DialogContent className="rounded-sm" data-testid="supplier-ship-success-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Shipment Created</DialogTitle>
            <DialogDescription>Write this code on your shipment paperwork - it's how the warehouse matches it on arrival. You can still edit its contents from the Shipments page until it's received.</DialogDescription>
          </DialogHeader>
          <div className="text-center py-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-sm">
            <div className="text-3xl font-data font-bold tracking-widest text-[#1E40AF]" data-testid="supplier-ship-success-code">{successCode}</div>
          </div>
          <Button onClick={() => setSuccessCode(null)} className="rounded-sm bg-[#1E40AF] hover:bg-[#1E3A8A] transition-colors duration-150" data-testid="supplier-ship-success-close-button">Done</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detailPoNumber} onOpenChange={(o) => !o && setDetailPoNumber(null)}>
        <DialogContent className="rounded-sm max-w-3xl" data-testid="supplier-po-detail-modal">
          <DialogHeader>
            <DialogTitle className="font-heading font-data">PO {detailPoNumber}</DialogTitle>
            <DialogDescription>
              {detailItems[0]?.buyer_entity_name} · Ordered {fmtDate(detailItems[0]?.po_date)}
              {detailItems[0]?.sap_po_number && <> · Printed PO #: <span className="font-data" data-testid="supplier-po-detail-printed-number">{detailItems[0].sap_po_number}</span></>}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse" data-testid="supplier-po-detail-table">
              <thead className="bg-[#F1F5F9] text-[#344054] text-xs font-bold font-heading uppercase tracking-wide">
                <tr>
                  <th className="border border-[#E2E8F0] p-1.5 text-left whitespace-nowrap">Item</th>
                  <th className="border border-[#E2E8F0] p-1.5 text-left whitespace-nowrap">Item Code</th>
                  <th className="border border-[#E2E8F0] p-1.5 text-left">Description</th>
                  <th className="border border-[#E2E8F0] p-1.5 text-left whitespace-nowrap">Ship To</th>
                  <th className="border border-[#E2E8F0] p-1.5 text-right whitespace-nowrap">PO Qty</th>
                  <th className="border border-[#E2E8F0] p-1.5 text-right whitespace-nowrap">Unit Price</th>
                  <th className="border border-[#E2E8F0] p-1.5 text-right whitespace-nowrap">Subtotal</th>
                  <th className="border border-[#E2E8F0] p-1.5 text-left whitespace-nowrap">Due Date</th>
                </tr>
              </thead>
              <tbody>
                {detailItems.map((it, i) => (
                  <tr key={it.item_number} className="bg-white odd:bg-[#F9FAFB]">
                    <td className="border border-[#E2E8F0] px-2 py-1.5 font-data">{it.item_number}</td>
                    <td className="border border-[#E2E8F0] px-2 py-1.5 font-data" data-testid={`supplier-po-detail-item-code-${it.item_number}`}>
                      {it.product_id || <span className="text-[#94A3B8] italic font-sans text-xs" title="This line is a Job Work item, not linked to a Product Master in SAP - no Item Code exists to show.">Job Work (no SAP code)</span>}
                    </td>
                    <td className="border border-[#E2E8F0] px-2 py-1.5">{it.description}</td>
                    <td className="border border-[#E2E8F0] px-2 py-1.5 font-data whitespace-nowrap" data-testid={`supplier-po-detail-ship-to-${it.item_number}`}>{it.ship_to_site_id || "—"}</td>
                    <td className="border border-[#E2E8F0] px-2 py-1.5 text-right font-data whitespace-nowrap">{it.po_qty} {it.unit_of_measure}</td>
                    <td className="border border-[#E2E8F0] px-2 py-1.5 text-right font-data whitespace-nowrap">{fmtUnitPrice(it.unit_price, it.currency)}</td>
                    <td className="border border-[#E2E8F0] px-2 py-1.5 text-right font-data whitespace-nowrap">{fmtMoney(it.subtotal, it.currency)}</td>
                    <td className="border border-[#E2E8F0] px-2 py-1.5 font-data whitespace-nowrap">{fmtDate(it.due_date)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={6} className="border border-[#E2E8F0] px-2 py-2 text-right font-semibold text-xs text-[#475569]">PO Total</td>
                  <td className="border border-[#E2E8F0] px-2 py-2 text-right font-data font-bold whitespace-nowrap">{fmtMoney(detailItems.reduce((s, it) => s + (it.subtotal || 0), 0), detailItems[0]?.currency)}</td>
                  <td className="border border-[#E2E8F0]"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </SupplierPortalLayout>
  );
}
